import React, { useEffect, useMemo, useRef, useState } from "react";
import { API, formatErr } from "@/lib/api";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bot, Loader2, Paperclip, Send, User, ShieldCheck, Download, X, MessageCircle, Sparkles } from "lucide-react";

const publicApi = axios.create({ baseURL: API });

const STORAGE_KEY = "muhasabaty_support_session_v2";

function loadStored() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
}
function saveStored(v) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch {}
}

export default function AISupportChat({ open, onClose }) {
  const [stored, setStored] = useState(loadStored());
  const [name, setName] = useState(stored?.name || "");
  const [phone, setPhone] = useState(stored?.phone || "");
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null); // {id, visitor_id, messages, pending}
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]); // {file_id,name,content_type,size}
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  // Init session if we have stored ids
  useEffect(() => {
    if (!open) return;
    if (stored?.session_id && stored?.visitor_id) {
      refreshSession(stored.session_id, stored.visitor_id);
    }
  // eslint-disable-next-line
  }, [open]);

  // Poll for admin replies every 5s while open
  useEffect(() => {
    if (!open || !session?.id) return;
    const t = setInterval(() => refreshSession(session.id, session.visitor_id, true), 5000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, [open, session?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session?.messages?.length]);

  const refreshSession = async (sid, vid, silent = false) => {
    try {
      const { data } = await publicApi.get("/support/session", { params: { session_id: sid, visitor_id: vid } });
      setSession(data);
    } catch (e) {
      if (!silent) {
        // Session probably deleted — clear and let user start over
        localStorage.removeItem(STORAGE_KEY);
        setSession(null); setStored(null);
      }
    }
  };

  const startSession = async () => {
    if (!name.trim()) return toast.error("الاسم مطلوب");
    setStarting(true);
    try {
      const { data } = await publicApi.post("/support/init", { name: name.trim(), phone: phone.trim(), lang: "ar" });
      const info = { session_id: data.session_id, visitor_id: data.visitor_id, name: name.trim(), phone: phone.trim() };
      saveStored(info); setStored(info);
      setSession({ id: data.session_id, visitor_id: data.visitor_id, messages: [], pending: false });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setStarting(false); }
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`الملف ${f.name} أكبر من 5 ميجا`); continue; }
        const b64 = await readFileAsBase64(f);
        const { data } = await publicApi.post("/support/upload", {
          name: f.name, content_type: f.type || "application/octet-stream", data_b64: b64,
        });
        setAttachments(prev => [...prev, data]);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAttachment = (fid) => setAttachments(prev => prev.filter(a => a.file_id !== fid));

  const sendMessage = async () => {
    if (!session) return;
    const txt = text.trim();
    if (!txt && attachments.length === 0) return;
    setSending(true);
    try {
      // Optimistic push
      setSession(prev => ({
        ...prev,
        messages: [...(prev?.messages || []), { role: "user", text: txt, attachments, created_at: new Date().toISOString() }],
      }));
      setText(""); setAttachments([]);
      const payload = { session_id: session.id, visitor_id: session.visitor_id, text: txt, attachments };
      const { data } = await publicApi.post("/support/message", payload);
      // Refresh full session (server-authoritative)
      await refreshSession(session.id, session.visitor_id, true);
      if (data.pending) toast.info("تم إرسال طلبك للمدير العام");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  const resetChat = () => {
    if (!confirm("بدء محادثة جديدة؟")) return;
    localStorage.removeItem(STORAGE_KEY);
    setSession(null); setStored(null); setName(""); setPhone("");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-2 md:p-4" data-testid="ai-support-chat">
      <div className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-2xl h-[85vh] md:h-[600px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/15 grid place-items-center relative">
              <Bot className="w-5 h-5" />
              <span className="absolute -bottom-0.5 -end-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-primary"></span>
            </div>
            <div>
              <div className="font-heading font-bold flex items-center gap-1.5">مساعد محاسبتي <Sparkles className="w-3.5 h-3.5" /></div>
              <div className="text-xs opacity-80">
                {session?.pending ? "بانتظار موافقة المدير العام" : "متصل الآن — الردود فورية"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {session && <Button size="sm" variant="ghost" className="text-primary-foreground hover:bg-white/10 h-8" onClick={resetChat} data-testid="reset-chat-btn">محادثة جديدة</Button>}
            <Button size="icon" variant="ghost" className="text-primary-foreground hover:bg-white/10 h-8 w-8" onClick={onClose} data-testid="close-chat-btn"><X className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Body */}
        {!session ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="w-full max-w-sm space-y-4">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3"><MessageCircle className="w-7 h-7 text-primary" /></div>
                <h3 className="font-heading font-bold text-lg">أهلاً بك 👋</h3>
                <p className="text-sm text-muted-foreground mt-1">تحدث مع مساعد محاسبتي الذكي، وسنعرّفك على التطبيق ونجيبك على كل استفسار. الطلبات الحساسة تُحوَّل تلقائياً للمدير العام للموافقة.</p>
              </div>
              <div className="space-y-2">
                <div><Label>الاسم *</Label><Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="اكتب اسمك" data-testid="visitor-name-input" /></div>
                <div><Label>الهاتف (اختياري)</Label><Input value={phone} onChange={(e)=>setPhone(e.target.value)} dir="ltr" className="font-mono" data-testid="visitor-phone-input" /></div>
              </div>
              <Button onClick={startSession} disabled={starting} className="w-full h-11" data-testid="start-chat-btn">
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : "ابدأ المحادثة"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30" data-testid="chat-messages">
              {(!session.messages || session.messages.length === 0) && (
                <div className="text-center text-sm text-muted-foreground py-8">
                  ابدأ بسؤالك... مثلاً: "ما هي مميزات التطبيق؟" أو "كيف أشترك؟"
                </div>
              )}
              {(session.messages || []).map((m, i) => <ChatBubble key={i} m={m} />)}
              {sending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-8 h-8 rounded-full bg-primary/10 grid place-items-center"><Bot className="w-4 h-4 text-primary" /></div>
                  <Loader2 className="w-4 h-4 animate-spin" /> يكتب المساعد...
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-border p-3 bg-background shrink-0">
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachments.map(a => (
                    <div key={a.file_id} className="text-xs px-2 py-1 bg-muted rounded flex items-center gap-1.5">
                      <Paperclip className="w-3 h-3" /> {a.name}
                      <button onClick={()=>removeAttachment(a.file_id)} className="text-destructive hover:text-destructive/80" data-testid={`remove-attach-${a.file_id}`}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input type="file" ref={fileRef} onChange={onPickFiles} className="hidden" multiple data-testid="chat-file-input" />
                <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" disabled={uploading} onClick={()=>fileRef.current?.click()} title="إرفاق ملف" data-testid="chat-attach-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </Button>
                <textarea
                  value={text} onChange={(e)=>setText(e.target.value)}
                  onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  rows={1} placeholder="اكتب رسالتك... (Enter للإرسال)"
                  className="flex-1 min-h-[44px] max-h-32 p-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  data-testid="chat-input"
                />
                <Button onClick={sendMessage} disabled={sending || (!text.trim() && attachments.length === 0)} className="h-11 shrink-0" data-testid="send-message-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              {session.pending && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> طلبك بانتظار موافقة المدير العام. سيصلك الرد هنا فور الاعتماد.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChatBubble({ m }) {
  const isUser = m.role === "user";
  const isAI = m.role === "ai";
  const isAdmin = m.role === "admin";
  const align = isUser ? "flex-row-reverse" : "";
  const bubble = isUser
    ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
    : isAdmin
      ? "bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl rounded-tl-sm"
      : "bg-background border border-border rounded-2xl rounded-tl-sm";
  const Icon = isUser ? User : (isAdmin ? ShieldCheck : Bot);
  return (
    <div className={`flex gap-2 ${align}`}>
      <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${isUser ? "bg-primary" : isAdmin ? "bg-emerald-600" : "bg-primary/10"}`}>
        <Icon className={`w-4 h-4 ${isUser || isAdmin ? "text-white" : "text-primary"}`} />
      </div>
      <div className={`max-w-[75%] px-3.5 py-2 ${bubble}`}>
        {isAdmin && <div className="text-[10px] font-bold mb-1 opacity-70">المدير العام{m.by ? ` — ${m.by}` : ""}{m.approved ? " · تمت الموافقة" : ""}</div>}
        {m.text && <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>}
        {(m.attachments || []).length > 0 && (
          <div className="mt-2 space-y-1">
            {m.attachments.map(a => (
              <a key={a.file_id} href={`${API}/support/file/${a.file_id}`} target="_blank" rel="noreferrer"
                 className={`text-xs flex items-center gap-1.5 underline ${isUser ? "text-white/90" : "text-primary"}`}
                 data-testid={`attach-link-${a.file_id}`}>
                <Download className="w-3 h-3" /> {a.name}
              </a>
            ))}
          </div>
        )}
        <div className="text-[10px] opacity-60 mt-1">
          {m.created_at ? new Date(m.created_at).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" }) : ""}
        </div>
      </div>
    </div>
  );
}
